import React, { useState, useEffect, useRef } from "react";
import { getEthersProvider } from "../web3/provider";
import { getPurchaseContract } from "../web3/contracts";
import { fmtUsdt, fmtScn } from "../utils/format";
import { ACTIVE_DEPLOY_BLOCK } from "../config/app.config";
import "./RecentActivity.css";

const WINDOW = 300000; // fallback recent blocks if deploy block isn't configured
const PAGE_SIZE = 10;

export default function RecentActivity({ userId }) {
  const [items, setItems] = useState(null);
  const [page, setPage] = useState(0);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    setPage(0);

    (async () => {
      try {
        const provider = getEthersProvider();
        const c = getPurchaseContract(provider);
        const latest = await provider.getBlockNumber();
        // Prefer the configured deploy block (full history); else recent window.
        const fromBlock = ACTIVE_DEPLOY_BLOCK > 0
          ? ACTIVE_DEPLOY_BLOCK
          : Math.max(0, latest - WINDOW);

        const q = (filter) => c.queryFilter(filter, fromBlock, latest).catch(() => []);

        const [direct, gen, matrix, claimed, activated, upgraded, retopup, accrued] =
          await Promise.all([
            q(c.filters.DirectReferralIncome(null, userId)),
            q(c.filters.GenerationLevelIncome(null, userId)),
            q(c.filters.MatrixLevelIncome(null, userId)),
            q(c.filters.SCNClaimed(userId)),
            q(c.filters.PackageActivated(userId)),
            q(c.filters.PackageUpgraded(userId)),
            q(c.filters.PackageRetoppedUp(userId)),
            q(c.filters.SCNAccrued(userId)),
          ]);

        const mapped = [];
        const push = (ev, kind, title, detail) =>
          mapped.push({
            block: ev.blockNumber,
            logIndex: ev.logIndex,
            key: `${ev.transactionHash}-${ev.logIndex}`,
            kind, title, detail,
          });

        // grossAmount = income before the 10% deduction
        direct.forEach((e) => push(e, "in", `+${fmtUsdt(e.args.grossAmount)} USDT`, `Direct from #${e.args.fromId}`));
        gen.forEach((e) => push(e, "in", `+${fmtUsdt(e.args.grossAmount)} USDT`, `Generation L${e.args.level}`));
        matrix.forEach((e) => push(e, "in", `+${fmtUsdt(e.args.grossAmount)} USDT`, `Matrix L${e.args.level}`));
        accrued.forEach((e) => push(e, "scn", `+${fmtScn(e.args.scnAmount)} SCN`, `Package #${e.args.packageId} reward`));
        claimed.forEach((e) => push(e, "scn", `Claimed ${fmtScn(e.args.amount)} SCN`, "SCN withdrawn"));
        activated.forEach((e) => push(e, "pkg", `Activated`, `Package #${e.args.packageId}`));
        upgraded.forEach((e) => push(e, "pkg", `Upgraded`, `Package #${e.args.packageId}`));
        retopup.forEach((e) => push(e, "pkg", `Re-topup`, `Package #${e.args.packageId}`));

        // newest first
        mapped.sort((a, b) => (b.block - a.block) || (b.logIndex - a.logIndex));

        if (!cancelled && mounted.current) setItems(mapped);
      } catch {
        if (!cancelled && mounted.current) setItems([]);
      }
    })();

    return () => { cancelled = true; };
  }, [userId]);

  const icon = (kind) => (kind === "in" ? "↘" : kind === "scn" ? "◈" : "⬆");

  const total = items ? items.length : 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const start = page * PAGE_SIZE;
  const slice = items ? items.slice(start, start + PAGE_SIZE) : [];

  return (
    <div className="card">
      <div className="section-title">Recent Activity</div>

      {items === null && <p className="muted">Loading activity…</p>}
      {items && total === 0 && <p className="muted">No recent activity yet.</p>}

      {total > 0 && (
        <>
          <div className="act-list">
            {slice.map((it) => (
              <div className="act-row" key={it.key}>
                <span className={`act-ico ${it.kind}`}>{icon(it.kind)}</span>
                <div className="act-main">
                  <div className="act-title">{it.title}</div>
                  <div className="act-detail muted">{it.detail}</div>
                </div>
                <span className="act-block">#{it.block}</span>
              </div>
            ))}
          </div>

          <div className="act-pager">
            <button
              className="pager-btn"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
            >
              ← Newer
            </button>
            <span className="pager-info">Page {page + 1} of {pages}</span>
            <button
              className="pager-btn"
              onClick={() => setPage((p) => Math.min(pages - 1, p + 1))}
              disabled={page >= pages - 1}
            >
              Older →
            </button>
          </div>
        </>
      )}
    </div>
  );
}
